import React, { useState, useMemo, useCallback, useEffect, useLayoutEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  HandHeart,
  Plus,
  CheckCircle2,
  Clock,
  Lock,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";

import { usePrayers } from "@/features/prayers/hooks";
import { getPendingPrayerRequests, approvePrayerRequest, discardPrayerRequest } from "../lib/api";
import { supabase } from "../lib/supabase";
import { useDistrictGuard } from "../hooks/useDistrictGuard";
import { usePullToRefresh } from "../hooks/usePullToRefresh";
import TopBar from "../components/TopBar";
import { InlineIconButton } from "../components/Forms";
import Badge from "../components/Badge";
import SearchInput from "../components/SearchInput";
import EmptyState from "../components/shared/EmptyState";
import { SkeletonListItemWithIcon } from "../components/shared/Skeletons";
import { useCustomLists } from "../lib/custom-lists";
import { formatSmartDate } from "../lib/date";
import { toast } from "@/lib/toast";
import { normalizeText } from "../lib/search-rank";
import OfflineItemBadge from "../components/OfflineItemBadge";
import { useCoordinatorChurchName } from "../hooks/useCoordinatorChurchName";
import { useConfirm } from "../components/ConfirmDialog";
import { useRegisterKeviaContext } from "../contexts/KeviaContext";

const TABS = [
  { k: "pendientes", label: "Por aprobar" },
  { k: "activas",    label: "Activas"     },
  { k: "historial",  label: "Historial"   },
];

/* ─── Hero espiritual ───────────────────────────────────────────────────── */

function PrayerHero({ activas, urgentes, district, churchName }) {
  return (
    <div className="px-3 pt-3 sm:px-4 bg-page-bg">
      <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[#312e81] via-[#3730a3] to-[#4338ca] p-4 shadow-[0_4px_20px_rgba(67,56,202,0.25)] dark:shadow-none" style={{ WebkitMaskImage: "-webkit-radial-gradient(white, black)" }}>
        <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-br from-white/[0.07] to-transparent pointer-events-none" />
        <div className="absolute inset-0 rounded-[inherit] overflow-hidden pointer-events-none">
          <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-8 -left-8 h-28 w-28 rounded-full bg-white/[0.06] blur-2xl" />
        </div>

        <div className="relative z-10 flex items-start justify-between gap-4">
          {/* Identidad del módulo */}
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="h-11 w-11 sm:h-12 sm:w-12 shrink-0 rounded-[12px] sm:rounded-[14px] bg-white/10 border border-white/20 flex items-center justify-center">
              <HandHeart size={20} className="text-white" />
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <h2 className="text-[18px] sm:text-[22px] font-semibold tracking-tight leading-tight text-white truncate">
                Muro de oración
              </h2>
              {(churchName || district?.name) && (
                <p className="mt-0.5 text-[14px] text-white/80">{churchName || district.name}</p>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="shrink-0 flex items-center gap-3 pt-1">
            <div className="text-right">
              <p className="text-[22px] font-bold leading-none text-white">{activas}</p>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-white/60 mt-1">Activas</p>
            </div>
            {urgentes > 0 && (
              <>
                <div className="w-px h-7 bg-white/20" />
                <div className="text-right">
                  <p className="text-[20px] font-bold leading-none text-amber-300">{urgentes}</p>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-white/55 mt-1">Urgentes</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Page ──────────────────────────────────────────────────────────────── */

export default function Prayers() {
  const location = useLocation();
  const { district, coordinatorChurchId } = useDistrictGuard();
  const coordinatorChurchName = useCoordinatorChurchName();

  const params     = new URLSearchParams(location.search);
  const urlFilter  = params.get("filter");

  const [confirm, ConfirmUI] = useConfirm();

  const [filter, setFilter] = useState(
    urlFilter || sessionStorage.getItem("keves:prayers:filter") || "activas"
  );

  const changeFilter = (k) => { setFilter(k); sessionStorage.setItem("keves:prayers:filter", k); };
  const [search,     setSearch]     = useState("");
  const [completing, setCompleting] = useState(null);

  const { allPrayers, loading, error, refetch, answer } = usePrayers({ filter: "todas" });
  const { containerRef, pullProgress, refreshing } = usePullToRefresh(refetch);

  const [pendingRequests,  setPendingRequests]  = useState([]);
  const [pendingLoading,   setPendingLoading]   = useState(false);
  const [approvingId,      setApprovingId]      = useState(null);
  const [discardingId,     setDiscardingId]     = useState(null);

  const loadPendingRequests = useCallback(async () => {
    if (!district?.id && !coordinatorChurchId) return;
    setPendingLoading(true);
    try {
      const data = await getPendingPrayerRequests(district?.id, coordinatorChurchId || null);
      const rows = data || [];

      const userIds = rows
        .filter(r => r.user_id && !r.is_anonymous)
        .map(r => r.user_id);

      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from("member_profiles")
          .select("user_id, display_name")
          .in("user_id", userIds);
        const nameMap = Object.fromEntries(
          (profiles || []).map(p => [p.user_id, p.display_name])
        );
        setPendingRequests(rows.map(r => ({ ...r, _displayName: nameMap[r.user_id] || null })));
      } else {
        setPendingRequests(rows);
      }
    } catch {
      toast.error("No se pudieron cargar las peticiones");
    } finally {
      setPendingLoading(false);
    }
  }, [district?.id, coordinatorChurchId]);

  useEffect(() => { loadPendingRequests(); }, [loadPendingRequests]);

  const handleApprove = useCallback(async (request) => {
    if (approvingId) return;
    setApprovingId(request.id);
    try {
      await approvePrayerRequest(request, district?.id);
      setPendingRequests(prev => prev.filter(r => r.id !== request.id));
      toast.success("Petición aprobada y publicada en el muro");
      refetch();
    } catch {
      toast.error("No se pudo aprobar");
    } finally {
      setApprovingId(null);
    }
  }, [approvingId, district?.id, refetch]);

  const handleDiscard = useCallback(async (id) => {
    if (discardingId) return;
    setDiscardingId(id);
    try {
      await discardPrayerRequest(id);
      setPendingRequests(prev => prev.filter(r => r.id !== id));
      toast.success("Petición descartada");
    } catch {
      toast.error("No se pudo descartar");
    } finally {
      setDiscardingId(null);
    }
  }, [discardingId]);

  useLayoutEffect(() => {
    try {
      const saved = sessionStorage.getItem("keves:prayers:scrollY");
      if (saved) { window.scrollTo(0, parseInt(saved, 10)); sessionStorage.removeItem("keves:prayers:scrollY"); }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    return () => { try { sessionStorage.setItem("keves:prayers:scrollY", String(Math.round(window.scrollY))); } catch { /* ignore */ } };
  }, []);

  const { getList } = useCustomLists();
  const PRAYER_CATEGORIES = getList("prayerCategories");

  const catInfo = useCallback(
    (v) => PRAYER_CATEGORIES.find((c) => c.value === v) || null,
    [PRAYER_CATEGORIES]
  );

  /* ── Stats ───────────────────────────────────────────────────────────── */
  const prayerStats = useMemo(() => {
    if (!allPrayers) return { activas: 0, urgentes: 0 };
    return {
      activas:  allPrayers.filter((p) => p.status === "activa").length,
      urgentes: allPrayers.filter((p) => p.priority === "alta" && p.status === "activa").length,
    };
  }, [allPrayers]);

  // Kevia dynamic context — registered while this page is mounted
  const keviaCtx = useMemo(() => {
    if (loading) return null;
    const filterLabels = { pendientes: "Por aprobar", activas: "Activas", historial: "Historial" };
    const lines = [
      "Módulo: Muro de oración",
      `  Filtro activo: ${filterLabels[filter] ?? filter}`,
      `  Oraciones activas: ${prayerStats.activas}`,
      prayerStats.urgentes > 0 && `  Urgentes: ${prayerStats.urgentes}`,
    ].filter(Boolean);
    return {
      type:         "module",
      id:           `prayers-${filter}`,
      name:         "Oraciones",
      tone:         prayerStats.urgentes > 0 ? "sensitive" : "neutral",
      contextBlock: `[Contexto activo en Kéves]\n${lines.join("\n")}\n\n[Pregunta del pastor]\n`,
    };
  }, [loading, filter, prayerStats]);
  useRegisterKeviaContext(keviaCtx);

  /* ── Quick-answer ────────────────────────────────────────────────────── */
  const handleAnswer = useCallback(async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (completing) return;
    const ok = await confirm({
      title:        "¿Marcar como respondida?",
      description:  "Esta oración se moverá al historial y no aparecerá en el muro activo.",
      confirmLabel: "Sí, fue respondida",
    });
    if (!ok) return;
    setCompleting(id);
    try {
      await answer(id);
      toast.success("¡Oración respondida!");
    } catch {
      toast.error("Error al actualizar");
    } finally {
      setCompleting(null);
    }
  }, [completing, confirm, answer]);

  const prayers = useMemo(() => {
    const list = (allPrayers || []).filter(p => p.status === 'activa');
    const searched = search.trim()
      ? list.filter(p => {
          const q = normalizeText(search);
          return normalizeText(p.text || '').includes(q) ||
            normalizeText(catInfo(p.category)?.label || '').includes(q);
        })
      : list;
    const urgent = searched.filter(p => p.priority === 'alta');
    const normal  = searched.filter(p => p.priority !== 'alta');
    return [...urgent, ...normal];
  }, [allPrayers, search, catInfo]);

  const respondidas = useMemo(() =>
    (allPrayers || []).filter(p => p.status === 'respondida'),
    [allPrayers]
  );

  return (
    <div
      ref={containerRef}
      className="anim-in min-h-[100dvh] bg-page-bg pb-[calc(96px+env(safe-area-inset-bottom))]"
      data-testid="prayers-page"
    >

      {ConfirmUI}

      {/* PULL INDICATOR */}
      {(pullProgress > 0 || refreshing) && (
        <div
          className="flex items-center justify-center overflow-hidden"
          style={{
            height: `${Math.min(pullProgress, 1) * 44}px`,
            opacity: Math.min(pullProgress * 1.4, 1),
            transition: refreshing ? "none" : "height 150ms ease-out, opacity 150ms ease-out",
          }}
        >
          <RefreshCw
            size={16}
            className={`text-slate-400 dark:text-slate-500 ${refreshing ? "animate-spin" : ""}`}
            style={{
              transform: `rotate(${Math.min(pullProgress, 1) * 220}deg) scale(${0.7 + Math.min(pullProgress, 1) * 0.3})`,
              transition: refreshing ? "none" : "transform 100ms ease-out",
            }}
          />
        </div>
      )}

      <TopBar
        title="Oraciones"
        subtitle={coordinatorChurchId ? (coordinatorChurchName || undefined) : district?.name}
        safeFallback="/district"
        right={
          <InlineIconButton icon={Plus} to="/prayers/new" accent="navy" aria-label="Nueva oración" data-testid="prayers-add-button" />
        }
      />

      {/* SEARCH — above hero */}
      <div className="px-4 pt-2 pb-1">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar oración..." />
      </div>

      {/* HERO — siempre visible durante carga para evitar layout shift en tabs */}
      {(loading || (allPrayers?.length ?? 0) > 0 || coordinatorChurchId) && (
        <PrayerHero
          activas={loading ? "—" : prayerStats.activas}
          urgentes={loading ? 0 : prayerStats.urgentes}
          district={district}
          churchName={coordinatorChurchId ? coordinatorChurchName : undefined}
        />
      )}

      {/* TABS */}
      <div className="px-4 pt-3">
        <div className="flex gap-1 bg-slate-200/50 dark:bg-slate-800/60 supports-[backdrop-filter]:bg-slate-200/30 dark:supports-[backdrop-filter]:bg-slate-800/40 backdrop-blur-xl rounded-full p-1 border border-slate-200/30 dark:border-slate-700/40">
          {TABS.map((t) => (
            <button
              key={t.k}
              onClick={() => changeFilter(t.k)}
              className={`flex-1 h-11 flex items-center justify-center gap-1 rounded-[20px] text-[11px] font-semibold transition-all duration-200 ease-out active:scale-[0.98] active:opacity-80 ${
                filter === t.k
                  ? "bg-white/90 dark:bg-slate-700/90 text-rose-700 dark:text-rose-400 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
              }`}
              data-testid={`prayers-filter-${t.k}`}
            >
              {t.label}
              {t.k === "pendientes" && pendingRequests.length > 0 && (
                <span className={`inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-[9px] font-bold ${filter === "pendientes" ? "bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400" : "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400"}`}>
                  {pendingRequests.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* MURO DE ORACIÓN */}
      <div className="px-4 pt-3 pb-5">

        {/* ── SECCIÓN PENDIENTES ─────────────────────────────────────────── */}
        {filter === "pendientes" && (
          <div className="space-y-3">
            {pendingLoading && [1, 2, 3].map(i => (
              <div key={i} className="h-28 rounded-[24px] bg-slate-200 dark:bg-slate-800 animate-pulse" />
            ))}

            {!pendingLoading && pendingRequests.length === 0 && (
              <EmptyState
                icon={<HandHeart size={28} />}
                title="Sin peticiones pendientes"
                description="Las peticiones de oración de los miembros aparecerán aquí para revisión."
              />
            )}

            {!pendingLoading && pendingRequests.map(req => (
              <div
                key={req.id}
                className="bg-white dark:bg-slate-800 rounded-[24px] p-4 border border-slate-200/50 dark:border-slate-700/50 shadow-[0_2px_12px_rgba(0,0,0,0.03)]"
              >
                <div className="flex items-center gap-2 mb-2.5">
                  <span className="text-[12px] font-semibold text-slate-600 dark:text-slate-300">
                    {req.is_anonymous ? "Anónimo" : req._displayName || "Miembro"}
                  </span>
                  <span className="text-[10px] text-slate-400 dark:text-slate-500 flex items-center gap-0.5">
                    <Clock size={9} />
                    {formatSmartDate(req.created_at)}
                  </span>
                </div>
                <p className="text-[14px] text-slate-700 dark:text-slate-200 leading-relaxed mb-4">
                  {req.content}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleApprove(req)}
                    disabled={approvingId === req.id || !!discardingId}
                    className="flex-1 h-9 rounded-[12px] bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-[12px] font-semibold border border-emerald-200/50 dark:border-emerald-800/30 transition-all duration-200 active:scale-[0.97] disabled:opacity-50"
                  >
                    {approvingId === req.id ? "···" : "Aprobar"}
                  </button>
                  <button
                    onClick={() => handleDiscard(req.id)}
                    disabled={discardingId === req.id || !!approvingId}
                    className="flex-1 h-9 rounded-[12px] bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 text-[12px] font-semibold border border-slate-200/50 dark:border-slate-700/50 transition-all duration-200 active:scale-[0.97] disabled:opacity-50"
                  >
                    {discardingId === req.id ? "···" : "Descartar"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── SECCIÓN ACTIVAS ───────────────────────────────────────────── */}
        {filter === "activas" && (
          <div className="space-y-3">
            {loading && (
              <div className="space-y-2.5 animate-in fade-in duration-300">
                {[1, 2, 3].map((i) => <SkeletonListItemWithIcon key={i} />)}
              </div>
            )}

            {!loading && error && (
              <EmptyState
                icon={<AlertTriangle size={28} />}
                title="No se pudo cargar"
                description="Verifica tu conexión e intenta de nuevo."
                actionLabel="Reintentar"
                actionOnClick={refetch}
              />
            )}

            {!loading && !error && prayers.length === 0 && (
              <EmptyState
                icon={<HandHeart size={28} />}
                title="El muro está en silencio"
                description="Registra las necesidades de oración de tu congregación."
                action={
                  <Link
                    to="/prayers/new"
                    className="inline-flex items-center gap-2 bg-[#1b4f9c] text-white rounded-[20px] px-6 py-3 font-semibold text-[15px] shadow-[0_4px_12px_rgba(27,79,156,0.25)] transition-all duration-200 ease-out active:scale-[0.98] active:opacity-80"
                    data-testid="prayers-empty-add"
                  >
                    <Plus size={16} />
                    Registrar oración
                  </Link>
                }
              />
            )}

            {!loading && prayers.length > 0 && (() => {
              const visibles = prayers.filter(p => p.community_visible);
              const privadas  = prayers.filter(p => !p.community_visible);
              const renderCard = (p) => {
                const cat        = catInfo(p.category);
                const urgent     = p.priority === "alta";
                const borderCls  = p.community_visible
                  ? "border-l-[3px] border-l-emerald-300 dark:border-l-emerald-600/40"
                  : urgent
                  ? "border-l-[3px] border-l-amber-300 dark:border-l-amber-600/40"
                  : "border-l-[3px] border-l-rose-200 dark:border-l-rose-800/30";
                const badgeTone  = urgent ? "orange" : "pink";
                const badgeLabel = urgent ? "Urgente" : "Activa";
                const personName = p.people?.name || p.families?.name || p.title || null;
                return (
                  <Link
                    key={p.id}
                    to={`/prayers/${p.id}`}
                    className={`list-item-lazy flex flex-col items-center text-center bg-white dark:bg-slate-800 rounded-[24px] px-3 pt-3 pb-2.5 border border-slate-200/50 dark:border-slate-700/60 ${borderCls} shadow-[0_2px_12px_rgba(0,0,0,0.03)] transition-all duration-300 ease-out hover:shadow-[0_6px_18px_rgba(0,0,0,0.06)] active:scale-[0.98] active:opacity-80`}
                    data-testid={`prayer-item-${p.id}`}
                  >
                    <div className="flex items-center justify-center gap-1.5 mb-1.5 w-full">
                      <span className="text-[10px] text-slate-400 dark:text-slate-500 flex items-center gap-0.5">
                        <Clock size={9} />
                        {formatSmartDate(p.date)}
                      </span>
                      {p.is_confidential && (
                        <Lock size={8} className="text-slate-300 dark:text-slate-600" />
                      )}
                    </div>
                    {personName && (
                      <p className="text-[10px] font-semibold text-violet-500 dark:text-violet-300 leading-tight mb-2 truncate w-full tracking-wide">
                        {personName}
                      </p>
                    )}
                    <p className="text-[12px] font-medium text-slate-800 dark:text-slate-100 leading-snug line-clamp-3 mb-2.5 flex-1">
                      {p.text}
                    </p>
                    <div className="flex flex-wrap justify-center gap-1 mb-2">
                      <Badge tone={badgeTone}>{badgeLabel}</Badge>
                      {cat && <Badge tone={cat.tone}>{cat.label}</Badge>}
                      <OfflineItemBadge item={p} />
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap justify-center">
                      <button
                        onClick={(e) => handleAnswer(e, p.id)}
                        disabled={!!completing}
                        className="inline-flex items-center gap-1 text-[9px] font-medium text-rose-400 dark:text-rose-500 border border-rose-200/60 dark:border-rose-800/40 rounded-full px-2.5 py-1 transition-all duration-200 hover:bg-rose-50 dark:hover:bg-rose-900/20 active:scale-[0.97] active:opacity-70 disabled:opacity-50"
                      >
                        <CheckCircle2 size={8} strokeWidth={2.5} />
                        {completing === p.id ? "···" : "Respondida"}
                      </button>
                    </div>
                  </Link>
                );
              };
              return (
                <>
                  {visibles.length > 0 && (
                    <>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="h-px flex-1 bg-slate-200/60 dark:bg-slate-700/60" />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                          Visibles en iglesia
                        </span>
                        <div className="h-px flex-1 bg-slate-200/60 dark:bg-slate-700/60" />
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                        {visibles.map(renderCard)}
                      </div>
                    </>
                  )}
                  {privadas.length > 0 && (
                    <>
                      <div className="flex items-center gap-3 mb-3 mt-4">
                        <div className="h-px flex-1 bg-slate-200/60 dark:bg-slate-700/60" />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                          Solo para el pastor
                        </span>
                        <div className="h-px flex-1 bg-slate-200/60 dark:bg-slate-700/60" />
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                        {privadas.map(renderCard)}
                      </div>
                    </>
                  )}
                </>
              );
            })()}

          </div>
        )}

        {/* ── SECCIÓN HISTORIAL ─────────────────────────────────────────── */}
        {filter === "historial" && (
          <div className="space-y-3">
            {loading && (
              <div className="space-y-2.5 animate-in fade-in duration-300">
                {[1, 2, 3].map((i) => <SkeletonListItemWithIcon key={i} />)}
              </div>
            )}

            {!loading && respondidas.length === 0 && (
              <EmptyState
                compact
                icon={<HandHeart size={20} />}
                title="Sin oraciones respondidas aún"
              />
            )}

            {!loading && respondidas.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                {respondidas.map((p) => {
                  const cat        = catInfo(p.category);
                  const personName = p.people?.name || p.families?.name || p.title || null;

                  return (
                    <Link
                      key={p.id}
                      to={`/prayers/${p.id}`}
                      className="list-item-lazy flex flex-col items-center text-center bg-white dark:bg-slate-800 rounded-[24px] px-3 pt-3 pb-2.5 border border-slate-200/50 dark:border-slate-700/60 border-l-[3px] border-l-violet-200 dark:border-l-violet-700/40 shadow-[0_2px_12px_rgba(0,0,0,0.03)] transition-all duration-300 ease-out hover:shadow-[0_6px_18px_rgba(0,0,0,0.06)] active:scale-[0.98] active:opacity-80"
                      data-testid={`prayer-item-${p.id}`}
                    >
                      <div className="flex items-center justify-center gap-1.5 mb-1.5 w-full">
                        <span className="text-[10px] text-slate-400 dark:text-slate-500 flex items-center gap-0.5">
                          <Clock size={9} />
                          {formatSmartDate(p.date)}
                        </span>
                        {p.is_confidential && (
                          <Lock size={8} className="text-slate-300 dark:text-slate-600" />
                        )}
                      </div>

                      {personName && (
                        <p className="text-[10px] font-semibold text-violet-500 dark:text-violet-300 leading-tight mb-2 truncate w-full tracking-wide">
                          {personName}
                        </p>
                      )}

                      <p className="text-[12px] font-medium text-slate-800 dark:text-slate-100 leading-snug line-clamp-3 mb-2.5 flex-1">
                        {p.text}
                      </p>

                      <div className="flex flex-wrap justify-center gap-1 mb-2">
                        <Badge tone="violet">Respondida</Badge>
                        {cat && <Badge tone={cat.tone}>{cat.label}</Badge>}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
